import React, { useState, useEffect } from 'react';
import { UserRound } from 'lucide-react';
import '../../style/PatientAvatar.css';

/**
 * Read-only circular patient avatar.
 *
 * Renders the patient's profile photo when a signed URL is provided, otherwise
 * a neutral gray person outline (the standard "no picture" placeholder). Used
 * both inside ProfilePictureCard and in list views (e.g. the scheduling page).
 *
 * Props:
 *  - url:   signed image URL, or null/undefined for the placeholder
 *  - size:  diameter in px (default 40)
 *  - alt:   accessible label
 *  - className: extra classes for the wrapper
 */
const PatientAvatar = ({ url, size = 40, alt = 'Patient', className = '' }) => {
  // Track load failure so an expired/broken signed URL falls back to the icon.
  const [errored, setErrored] = useState(false);

  // Reset the error state whenever the source changes (e.g. after a re-capture).
  useEffect(() => { setErrored(false); }, [url]);

  const showImage = url && !errored;

  return (
    <div
      className={`patient-avatar ${className}`}
      style={{ width: size, height: size }}
    >
      {showImage ? (
        <img
          src={url}
          alt={alt}
          className="patient-avatar-img"
          onError={() => setErrored(true)}
        />
      ) : (
        <UserRound
          className="patient-avatar-placeholder"
          size={Math.round(size * 0.58)}
          strokeWidth={1.75}
          aria-label="No profile picture"
        />
      )}
    </div>
  );
};

export default PatientAvatar;
